import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AppSeo } from "@/components/AppSeo";
import { EMPTY_DRAFT, type Draft } from "./adminChangelog/types";
import { useAdminChangelog } from "./adminChangelog/useAdminChangelog";
import { ChangelogEntriesList } from "./adminChangelog/ChangelogEntriesList";
import { ChangelogEntryDialog } from "./adminChangelog/ChangelogEntryDialog";

const AdminChangelog = () => {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { entries, isLoading, saveMutation, togglePublishMutation, deleteMutation } =
    useAdminChangelog({
      onSaved: () => setDraft(null),
      onDeleted: () => setDeleteId(null),
    });

  const deleteTarget = entries?.find((e) => e.id === deleteId) ?? null;

  return (
    <div className="space-y-6">
      <AppSeo title="Changelog · Admin" />
      <div className="flex items-center justify-between">
        <Button asChild variant="ghost" size="sm" className="gap-2">
          <Link to="/admin"><ArrowLeft className="h-4 w-4" /> Admin</Link>
        </Button>
        <Button asChild variant="outline" size="sm" className="gap-2">
          <Link to="/changelog" target="_blank">
            <ExternalLink className="h-4 w-4" /> View public page
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle>Changelog entries</CardTitle>
            <CardDescription>
              Published entries appear on /changelog and bump the in-app "What's new" badge.
            </CardDescription>
          </div>
          <Button onClick={() => setDraft({ ...EMPTY_DRAFT })} className="gap-2">
            <Plus className="h-4 w-4" /> New entry
          </Button>
        </CardHeader>
        <CardContent>
          <ChangelogEntriesList
            entries={entries}
            isLoading={isLoading}
            togglePending={togglePublishMutation.isPending}
            onTogglePublish={(entry) => togglePublishMutation.mutate(entry)}
            onEdit={(entry) => setDraft({
              id: entry.id, slug: entry.slug, title: entry.title,
              summary: entry.summary, body: entry.body, category: entry.category,
              published: Boolean(entry.published_at),
            })}
            onDelete={(id) => setDeleteId(id)}
          />
        </CardContent>
      </Card>

      <ChangelogEntryDialog
        draft={draft}
        onChange={setDraft}
        saving={saveMutation.isPending}
        onSave={(d) => saveMutation.mutate(d)}
      />

      <AlertDialog open={deleteId !== null} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this entry?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.title}" will be removed permanently. Read receipts for it are also deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminChangelog;
